import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Home,
  Package,
  ShoppingCart,
  FileText,
  BarChart3,
  Users,
  X,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Shield,
  LogOut,
  Wrench,
  BookOpen,
  AlertTriangle,
  Layers,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import useAuthStore from '@/stores/authStore';

// Mapa de nombres de icono (string del backend) → componente Lucide.
// Los módulos custom creados por admin pueden usar cualquiera de estos.
const ICON_MAP: Record<string, LucideIcon> = {
  Home, Package, ShoppingCart, FileText, BarChart3, Users,
  ClipboardList, Shield, Wrench, BookOpen, Layers, Settings,
};

const renderIcon = (iconName?: string): React.ReactNode => {
  const Icon = (iconName && ICON_MAP[iconName]) || Layers;
  return <Icon className="w-5 h-5" />;
};

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  user?: {
    name: string;
    email: string;
    role: string;
  };
}

// Códigos de módulo cuyos paths se calculan en runtime según el usuario
// (issues #31, #33). Para el resto, usamos `ruta` que viene del backend.
const DYNAMIC_PATH_CODES = new Set(['cotizaciones', 'pedidos']);

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, user }) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { logout } = useAuth();
  const { user: authUser, stopImpersonate } = useAuthStore();

  // Issue #14 Fix: No usar 'Administrador' como default, usar el rol real del usuario
  const defaultUser = {
    name: user?.name || 'Usuario',
    email: user?.email || 'usuario@mundi.com',
    role: user?.role || 'user',  // Default a 'user' no 'admin'
  };

  // RBAC dinámico: módulos vienen del backend (`modulos_accesibles`).
  // Si estamos cargando o el backend aún no respondió, lista vacía hasta que llegue.
  const modulos = authUser?.modulos_accesibles || [];
  const navModulos = modulos.filter((m) => m.grupo === 'principal');
  const adminModulos = modulos.filter((m) => m.grupo === 'admin');

  // Issues #31, #33: dynamic paths sólo para cotizaciones y pedidos cuando
  // el usuario tiene exactamente 1 cliente asignado.
  const getDynamicPath = (codigo: string, fallbackRuta?: string): string => {
    if (!authUser || !DYNAMIC_PATH_CODES.has(codigo)) return fallbackRuta || '/dashboard';
    const clientes = authUser.permisos?.clientes || [];
    const isAdmin = authUser.es_admin || authUser.rol === 'admin';

    if (codigo === 'cotizaciones') {
      if (isAdmin) return '/cotizaciones';
      if (clientes.length === 1) return `/cotizaciones/cliente/${clientes[0]}`;
      return '/cotizaciones';
    }
    if (codigo === 'pedidos') {
      if (isAdmin) return '/pedidos-guardados';
      if (clientes.length === 1) return `/pedidos/cliente/${clientes[0]}`;
      return '/pedidos-guardados';
    }
    return fallbackRuta || '/dashboard';
  };

  const handleStopImpersonate = async () => {
    try {
      await stopImpersonate();
      toast.success('Impersonation detenida');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Error al detener impersonation');
    }
  };

  return (
    <>
      {/* Mobile/Tablet overlay - Solo visible en < lg cuando está abierto */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Siempre fixed, visible en lg+ */}
      <aside
        className={`
          fixed
          top-0 left-0 z-50 h-screen
          bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700
          transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
          ${isCollapsed ? 'w-20' : 'w-64'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header with logo */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            {isCollapsed ? (
              <div className="flex items-center justify-center w-full">
                <img
                  src="/logo.png"
                  alt="MundiRep"
                  className="w-8 h-8 object-contain"
                />
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <img
                  src="/logo.png"
                  alt="MundiRep"
                  className="w-8 h-8 object-contain"
                />
                <span className="text-xl font-bold text-gray-800 dark:text-gray-100">Sistema Mundi</span>
              </div>
            )}

            {/* Mobile close button */}
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>

            {/* Desktop collapse button */}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:block p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {isCollapsed ? (
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>
          </div>

          {/* User info */}
          <div className={`p-4 border-b border-gray-200 dark:border-gray-700 ${isCollapsed ? 'hidden' : 'block'}`}>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <span className="text-blue-600 dark:text-blue-400 font-semibold">
                  {defaultUser.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {defaultUser.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{defaultUser.role}</p>
              </div>
            </div>
          </div>

          {/* Banner de impersonation activa */}
          {authUser?.impersonated && !isCollapsed && (
            <div className="mx-4 mt-3 p-2 rounded-lg bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700 text-xs">
              <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">Impersonando</p>
                  <p className="truncate">como {authUser.username}</p>
                  <button
                    onClick={handleStopImpersonate}
                    className="mt-1 underline hover:no-underline"
                  >
                    Volver a {authUser.impersonator_username}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Navigation — render dinámico desde RBAC */}
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-1">
              {navModulos.map((mod) => {
                const itemPath = DYNAMIC_PATH_CODES.has(mod.codigo)
                  ? getDynamicPath(mod.codigo, mod.ruta)
                  : (mod.ruta || '/dashboard');
                const isActive = location.pathname === itemPath;
                return (
                  <li key={mod.codigo}>
                    <Link
                      to={itemPath}
                      onClick={() => {
                        if (window.matchMedia('(max-width: 1023px)').matches) {
                          onClose();
                        }
                      }}
                      className={`
                        flex items-center space-x-3 px-3 py-2 rounded-lg
                        transition-colors duration-200
                        ${isActive
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}
                        ${isCollapsed ? 'justify-center' : ''}
                      `}
                      title={isCollapsed ? mod.nombre : ''}
                    >
                      <span className={isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}>
                        {renderIcon(mod.icono)}
                      </span>
                      {!isCollapsed && (
                        <span className="font-medium">{mod.nombre}</span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Admin Section — solo si hay módulos del grupo 'admin' accesibles */}
            {adminModulos.length > 0 && (
              <>
                {!isCollapsed && (
                  <div className="mt-6 mb-2 px-3">
                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                      Administración
                    </p>
                  </div>
                )}
                <ul className="space-y-1">
                  {adminModulos.map((mod) => {
                    const itemPath = mod.ruta || '/dashboard';
                    const isActive = location.pathname === itemPath;
                    return (
                      <li key={mod.codigo}>
                        <Link
                          to={itemPath}
                          onClick={() => {
                            if (window.matchMedia('(max-width: 1023px)').matches) {
                              onClose();
                            }
                          }}
                          className={`
                            flex items-center space-x-3 px-3 py-2 rounded-lg
                            transition-colors duration-200
                            ${isActive
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}
                            ${isCollapsed ? 'justify-center' : ''}
                          `}
                          title={isCollapsed ? mod.nombre : ''}
                        >
                          <span className={isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}>
                            {renderIcon(mod.icono)}
                          </span>
                          {!isCollapsed && (
                            <span className="font-medium">{mod.nombre}</span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </nav>

          {/* Footer - Issue #25: Agregar botón Cerrar Sesión */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              title="Cerrar sesión"
            >
              <LogOut className="w-4 h-4" />
              {!isCollapsed && <span>Cerrar Sesión</span>}
            </button>
            {!isCollapsed && (
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-3">
                Sistema Mundi v1.0.0
              </p>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
